
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export const useMapboxToken = () => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Function to fetch token with retry
  const fetchToken = useCallback(async (retryCount = 0, maxRetries = 3) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase.functions.invoke('location', {
        body: { action: 'getMapboxToken' }
      });
      
      if (error) {
        console.error("Error invoking location function:", error);
        
        // If we haven't reached max retries, try again with exponential backoff
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s, etc.

          toast({
            title: "Connection issue",
            description: `Trying to reconnect... (${retryCount + 1}/${maxRetries})`,
          });
          
          setTimeout(() => fetchToken(retryCount + 1, maxRetries), delay);
          return;
        }
        
        toast({
          title: "Error",
          description: error.message || 'Failed to fetch Mapbox token',
          variant: "destructive",
        });
        throw new Error(error.message || 'Failed to fetch Mapbox token');
      }
      
      if (data?.mapboxToken) {
        setMapboxToken(data.mapboxToken);
        // Store in localStorage for development convenience
        localStorage.setItem('mapbox_token', data.mapboxToken);
      } else {
        console.error("No token returned from edge function", data);
        
        // If we haven't reached max retries, try again with exponential backoff
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1000;

          setTimeout(() => fetchToken(retryCount + 1, maxRetries), delay);
          return;
        }

        toast({
          title: "Error",
          description: 'No Mapbox token returned from the server',
          variant: "destructive",
        });
        setError('No Mapbox token returned from the server');
      }
    } catch (err) {
      console.error('Error fetching Mapbox token:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Mapbox token');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);
  
  // Validate stored token is still functional
  const validateStoredToken = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('location', {
        body: { action: 'health' }
      });

      // Fix: Compare the status string with 'ok' instead of a boolean
      if (error || data?.status !== 'ok') {
        console.warn("Stored token may not be valid, clearing it");
        // Don't clear yet, but fetch a new one in the background
        fetchToken();
      }
    } catch (err) {
      console.error("Error validating stored token:", err);
      // Continue using stored token anyway, don't want to interrupt the user
    }
  }, [fetchToken]);

  useEffect(() => {
    // First check if we have the token in localStorage (for development convenience)
    const storedToken = localStorage.getItem('mapbox_token');
    if (storedToken) {
      setMapboxToken(storedToken);
      setIsLoading(false);
      // Also verify the token is still valid by pinging the health endpoint
      validateStoredToken();
      return;
    }

    // If not, fetch from our secure edge function
    fetchToken();
  }, [fetchToken, validateStoredToken]);
  
  const clearToken = useCallback(() => {
    localStorage.removeItem('mapbox_token');
    setMapboxToken(null);
    setError(null);
    toast({
      title: "Token cleared",
      description: "Mapbox token has been cleared. Fetching new token...",
    });
    // Fetch a new token
    fetchToken();
  }, [fetchToken, toast]);
  
  return { 
    mapboxToken, 
    isLoading, 
    error, 
    clearToken,
    refreshToken: () => fetchToken()
  };
};
